import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import ChartPanel from '../charts/ChartPanel'
import { DASHBOARD_PLATFORM_LABELS } from './utils/dashboardPlatformIcons'
import {
  barPlacement,
  buildGrowthAxis,
  formatGrowthTick,
  growthToneClass,
  isGrowthMetric,
  readPlatformRaw,
} from './utils/socialPerformanceBenchmarkAxis'
import './SocialPerformanceBenchmark.css'

const PLATFORM_COLORS = {
  facebook: '#1877f2',
  instagram: '#e1306c',
  linkedin: '#4f46e5',
  twitter: '#536471',
  tiktok: '#00b4d8',
}

const PLATFORM_COLOR_VARS = {
  facebook: '--sp-platform-facebook',
  instagram: '--sp-platform-instagram',
  linkedin: '--sp-platform-linkedin',
  twitter: '--sp-platform-twitter',
  tiktok: '--sp-platform-tiktok',
}

function encodePlatformSummary(row, platform, metrics) {
  return metrics
    .map((metric) => {
      const value = row?.platformMetrics?.[platform]?.[metric.key]?.display ?? '0'
      const raw = row?.platformMetrics?.[platform]?.[metric.key]?.raw
      return `${metric.key}\t${metric.label}\t${value}\t${raw ?? ''}`
    })
    .join('\n')
}

function PlatformLegendMark({ platform, label }) {
  return (
    <span className="socialPerformanceTooltipLegend">
      <span
        className="campaignPerformanceLegendSwatch"
        style={{ background: PLATFORM_COLORS[platform] || PLATFORM_COLORS.twitter }}
        aria-hidden="true"
      />
      <span>{label || DASHBOARD_PLATFORM_LABELS[platform] || platform}</span>
    </span>
  )
}

const SocialPerformanceBenchmark = ({
  tabs,
  activeTab,
  onTabChange,
  section,
  visiblePlatforms = [],
  loading = false,
}) => {
  const tooltipId = useId()
  const metrics = section?.metrics || []
  const rows = section?.rows || []
  const chartMetric = metrics.find((metric) => isGrowthMetric(metric)) || metrics[0]
  const chartMetricKey = chartMetric?.key
  const chartMetricLabel = chartMetric?.label || 'Growth'

  const connectedPlatforms = useMemo(() => {
    const all = ['facebook', 'instagram', 'linkedin', 'twitter', 'tiktok']
    const fromSection = section?.connectedPlatforms
    if (Array.isArray(fromSection) && fromSection.length > 0) {
      return fromSection.filter((platform) => all.includes(platform))
    }
    const active = new Set()
    rows.forEach((row) => {
      Object.keys(row.platformMetrics || {}).forEach((platform) => {
        if (all.includes(platform)) active.add(platform)
      })
    })
    return all.filter((platform) => active.has(platform))
  }, [section?.connectedPlatforms, rows])

  const availablePlatforms = useMemo(() => {
    if (!connectedPlatforms.length) return []
    const selected = (visiblePlatforms || []).filter((platform) => connectedPlatforms.includes(platform))
    return selected.length > 0 ? selected : connectedPlatforms
  }, [visiblePlatforms, connectedPlatforms])

  const [activePlatforms, setActivePlatforms] = useState(() => availablePlatforms)

  useEffect(() => {
    setActivePlatforms((current) => {
      const stillVisible = current.filter((platform) => availablePlatforms.includes(platform))
      if (stillVisible.length > 0) return stillVisible
      return availablePlatforms
    })
  }, [availablePlatforms])

  const platforms = activePlatforms.length > 0 ? activePlatforms : availablePlatforms

  const togglePlatform = (platform) => {
    setActivePlatforms((current) => {
      if (current.includes(platform)) {
        if (current.length === 1) return current
        return current.filter((item) => item !== platform)
      }
      return availablePlatforms.filter((item) => item === platform || current.includes(item))
    })
  }

  const axis = useMemo(() => {
    const values = []
    rows.forEach((row) => {
      platforms.forEach((platform) => {
        values.push(readPlatformRaw(row, platform, chartMetricKey))
      })
    })
    return buildGrowthAxis(values)
  }, [rows, platforms, chartMetricKey])

  const scrollRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isScrollable, setIsScrollable] = useState(false)

  const updateScroll = useCallback(() => {
    const node = scrollRef.current
    if (!node) return
    const { scrollLeft, scrollWidth, clientWidth } = node
    setIsScrollable(scrollWidth > clientWidth + 1)
    setCanScrollLeft(scrollLeft > 4)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4)
  }, [])

  useEffect(() => {
    const node = scrollRef.current
    if (!node) return undefined
    updateScroll()
    const observer = new ResizeObserver(updateScroll)
    observer.observe(node)
    node.addEventListener('scroll', updateScroll, { passive: true })
    window.addEventListener('resize', updateScroll)
    return () => {
      observer.disconnect()
      node.removeEventListener('scroll', updateScroll)
      window.removeEventListener('resize', updateScroll)
    }
  }, [updateScroll, platforms, metrics, rows])

  const scrollBoard = (direction) => {
    const node = scrollRef.current
    if (!node) return
    const delta = direction === 'right' ? node.clientWidth * 0.75 : -node.clientWidth * 0.75
    node.scrollBy({ left: delta, behavior: 'smooth' })
  }

  const platformCount = platforms.length
  const needsColumnScroll = platformCount > 2
  const columnMinWidth = needsColumnScroll
    ? 16 + platformCount * 50 + Math.max(0, platformCount - 1) * 8
    : 0
  const columnTemplate = `repeat(${Math.max(rows.length, 1)}, minmax(${columnMinWidth}px, 1fr))`
  const isEmpty = !loading && rows.length === 0

  return (
    <div className="socialPerformanceSection">
      <ChartPanel
        dataChartId="socialPerformanceBenchmark"
        title="Social Performance: You vs Competitors"
        subtitle="View how you compare against competitors and your brand's average property. Data is sourced from public, organic performance."
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        loading={loading}
        empty={isEmpty}
        emptyMessage="No data available"
      >
        <div className="socialPerformanceBreakup">
          <div
            className="socialPerformanceBoard"
            style={{ '--sp-metric-count': metrics.length || 3 }}
          >
            <div className="socialPerformanceBoardPinned">
              <div className="socialPerformanceBoardHeadSpacer" />
              <div className="socialPerformanceAxis">
                <div className="socialPerformanceAxisTitle">{chartMetricLabel}</div>
                <div className="socialPerformanceAxisTicks">
                  {axis.ticks.map((tick) => (
                    <span
                      key={tick}
                      className="socialPerformanceAxisTick"
                      style={{ top: `${((axis.yMax - tick) / (axis.yMax - axis.yMin || 1)) * 100}%` }}
                    >
                      {formatGrowthTick(tick)}
                    </span>
                  ))}
                </div>
              </div>
              {metrics.map((metric) => (
                <div key={metric.key} className="socialPerformanceMetricLabel">
                  {metric.label}
                </div>
              ))}
            </div>

            <div
              className={`socialPerformanceBoardScrollWrap${isScrollable ? ' is-scrollable' : ''}${needsColumnScroll ? ' socialPerformanceBoardScrollWrap--wide' : ' socialPerformanceBoardScrollWrap--fit'}`}
              style={{ '--sp-column-min': `${columnMinWidth}px` }}
            >
              {isScrollable && canScrollLeft && (
                <button
                  type="button"
                  className="socialPerformanceScrollHint socialPerformanceScrollHint--left"
                  onClick={() => scrollBoard('left')}
                  aria-label="Scroll properties left"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
              {isScrollable && canScrollRight && (
                <button
                  type="button"
                  className="socialPerformanceScrollHint socialPerformanceScrollHint--right"
                  onClick={() => scrollBoard('right')}
                  aria-label="Scroll properties right"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
              <div
                className="socialPerformanceBoardScroll"
                ref={scrollRef}
                aria-label="Property comparison (scroll horizontally)"
              >
                <div
                  className="socialPerformanceBoardGrid"
                  style={{ '--sp-columns': columnTemplate }}
                >
                  <div className="socialPerformancePlotGrid" aria-hidden="true">
                    {axis.ticks.map((tick) => (
                      <span
                        key={tick}
                        className={`socialPerformancePlotLine ${tick === 0 ? 'is-zero' : ''}`}
                        style={{ top: `${((axis.yMax - tick) / (axis.yMax - axis.yMin || 1)) * 100}%` }}
                      />
                    ))}
                  </div>

                  {rows.map((row, index) => (
                    <div
                      key={`${row.brandId || row.brand}-${row.rank}`}
                      className={`socialPerformanceProperty ${row.isYou ? 'is-you' : ''} ${row.isBrandAverage ? 'is-average' : ''}`}
                      style={{ gridColumn: index + 1 }}
                    >
                      <div className="socialPerformancePropertyHead">
                        <div className="socialPerformancePropertyRank">#{row.rank}</div>
                        <div className="socialPerformancePropertyName">{row.brand}</div>
                        <div className="socialPerformancePropertyBadge">
                          {row.isYou && <span className="socialPerformanceYouTag">YOU</span>}
                          {row.isBrandAverage && (
                            <span className="socialPerformanceSubBrandTag">Brand Average</span>
                          )}
                        </div>
                        <div className="socialPerformancePropertySub">
                          {row.brandSubline || row.subBrand || ''}
                        </div>
                      </div>
                      <div className="socialPerformancePropertyBars">
                        <div className="socialPerformanceBarCluster">
                          {platforms.map((platform) => {
                            const raw = readPlatformRaw(row, platform, chartMetricKey)
                            const platformLabel = DASHBOARD_PLATFORM_LABELS[platform] || platform
                            return (
                              <span
                                key={platform}
                                className="socialPerformanceBarTrack"
                                data-tooltip-id={tooltipId}
                                data-sp-brand={row.brand}
                                data-sp-platform={platformLabel}
                                data-sp-platform-key={platform}
                                data-sp-metrics={encodePlatformSummary(row, platform, metrics)}
                              >
                                <span
                                  className={`socialPerformanceBar ${raw < 0 ? 'is-negative' : ''}`}
                                  style={{
                                    ...barPlacement(raw, axis.yMin, axis.yMax),
                                    background: `var(${PLATFORM_COLOR_VARS[platform]})`,
                                  }}
                                />
                              </span>
                            )
                          })}
                        </div>
                      </div>
                      {metrics.map((metric) => (
                        <div key={metric.key} className="socialPerformancePropertyMetric">
                          {platforms.map((platform) => {
                            const platformLabel = DASHBOARD_PLATFORM_LABELS[platform] || platform
                            const toneClass = isGrowthMetric(metric)
                              ? growthToneClass(row.platformMetrics?.[platform]?.[metric.key]?.raw)
                              : ''
                            return (
                              <span
                                key={platform}
                                className={`socialPerformancePropertyValue dashboard-tabular-nums ${toneClass}`.trim()}
                                data-tooltip-id={tooltipId}
                                data-sp-brand={row.brand}
                                data-sp-platform={platformLabel}
                                data-sp-platform-key={platform}
                                data-sp-metrics={encodePlatformSummary(row, platform, metrics)}
                              >
                                <span
                                  className="campaignPerformanceLegendSwatch socialPerformanceMetricSwatch"
                                  style={{ background: `var(${PLATFORM_COLOR_VARS[platform]})` }}
                                  aria-hidden="true"
                                />
                                {row.platformMetrics?.[platform]?.[metric.key]?.display ?? '0'}
                              </span>
                            )
                          })}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="socialPerformanceChartLegendRow">
            <div className="campaignPerformanceLegend socialPerformanceChartLegend" role="group" aria-label="Platform legend">
              {availablePlatforms.map((platform) => {
                const isActive = platforms.includes(platform)
                return (
                  <button
                    key={platform}
                    type="button"
                    className={`campaignPerformanceLegendItem socialPerformanceChartLegendItem ${isActive ? 'is-active' : 'is-muted'}`}
                    aria-pressed={isActive}
                    onClick={() => togglePlatform(platform)}
                  >
                    <span
                      className="campaignPerformanceLegendSwatch"
                      style={{ background: `var(${PLATFORM_COLOR_VARS[platform]})` }}
                      aria-hidden="true"
                    />
                    <span className="socialPerformanceChartLegendLabel">
                      {DASHBOARD_PLATFORM_LABELS[platform] || platform}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <Tooltip
            id={tooltipId}
            className="socialPerformanceTableTooltip"
            place="top"
            positionStrategy="fixed"
            variant="light"
            offset={8}
            style={{ zIndex: 10000 }}
            render={({ activeAnchor }) => {
              if (!activeAnchor) return null
              const brand = activeAnchor.getAttribute('data-sp-brand') || ''
              const platformKey = activeAnchor.getAttribute('data-sp-platform-key') || ''
              const platform = activeAnchor.getAttribute('data-sp-platform') || ''
              const summary = (activeAnchor.getAttribute('data-sp-metrics') || '')
                .split('\n')
                .map((line) => line.split('\t'))
                .filter((parts) => parts[0])
              return (
                <div className="socialPerformanceTableTooltipBody">
                  <div className="socialPerformanceTableTooltipBrand">{brand}</div>
                  <div className="socialPerformanceTableTooltipMeta">
                    <PlatformLegendMark platform={platformKey} label={platform} />
                  </div>
                  {summary.length > 0 && (
                    <div className="socialPerformanceTableTooltipSplit">
                      {summary.map(([metricKey, label, value, raw]) => (
                        <div key={metricKey} className="socialPerformanceTableTooltipSplitRow">
                          <span>{label}</span>
                          <span className={isGrowthMetric({ key: metricKey }) ? growthToneClass(raw) : ''}>
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            }}
          />

          {section?.strategicInsight && (
            <div className="audienceInsightBlock">
              <div className="audienceInsightBlockLabel">Insight</div>
              <p className="audienceInsightBlockText">{section.strategicInsight}</p>
            </div>
          )}
        </div>
      </ChartPanel>
    </div>
  )
}

export default SocialPerformanceBenchmark
